// ./src/components/features/EventGuests/answerFormat.ts
//
// Read-only rendering of a guest's answer to a registration question. Extracted from
// GuestAnswersMatrix so the approval review drawer shows answers the SAME way the Answers view
// does — a host judging an application and a host scanning the matrix must not see two different
// renderings of the same response (a raw option UUID in one and its label in the other).

import { RsvpQuestion, RsvpQuestionAnswer } from "@/utils/api";

const NUMBER_VTS = new Set(["integer", "decimal", "amount"]);

/** A2e #91 — where a picture answer is: the thumbnail to show and the full picture to open. */
export interface AnswerPictureSource {
  src: string;
  href: string;
}

/** A2e #91 — the picture a picture question's answer holds, or null (any other type, no answer, no file). */
export const answerPicture = (q: RsvpQuestion, a: RsvpQuestionAnswer | undefined): AnswerPictureSource | null => {
  if (q.questionType !== "image" || !a?.answerImageFile) {
    return null;
  }

  const full = a.answerImageFile.fileUrl || a.answerImageFile.thumbnailUrl || "";
  const thumb = a.answerImageFile.thumbnailUrl || full;

  return full ? { src: thumb, href: full } : null;
};

/** A compact, read-only rendering of one guest's answer to one question. */
export const formatAnswer = (q: RsvpQuestion, a: RsvpQuestionAnswer | undefined): string => {
  if (!a) return "";
  // A2e #91 — a picture, as text, is its link (a view that can draw it uses `answerPicture` instead).
  if (q.questionType === "image") return answerPicture(q, a)?.href ?? "";
  // A choice answer stores the option's id (or its optionValue); resolve to the
  // human label so we never show a raw UUID.
  const optionLabel = (val: unknown): string => {
    const opt = (q.options ?? []).find((o) => o.id === val || o.optionValue === val);
    return opt?.label ?? opt?.optionValue ?? String(val ?? "");
  };
  if (q.questionType === "multi_choice") {
    const raw = Array.isArray(a.answerJson) ? (a.answerJson as unknown[]) : [];
    return raw.map(optionLabel).filter(Boolean).join(", ");
  }
  if (q.questionType === "single_choice") {
    // Canonical single choice is in answer_text; older public RSVPs stored it in
    // answer_json (array of one) — accept either so both render the option label.
    const val = a.answerText ?? (Array.isArray(a.answerJson) ? a.answerJson[0] : null);
    return val ? optionLabel(val) : "";
  }
  // A2e #86 — an address prints as the server's one-line copy ("12 Main St, Toronto, ON M5V 2T6, Canada"). Decided by
  // the TYPE, before the value type: an address question still carries a stored value_type.
  if (q.questionType === "address") return a.answerText ?? "";
  if (q.valueType === "boolean") {
    if (a.answerBoolean == null) return "";
    return a.answerBoolean ? "Yes" : "No";
  }
  if (q.valueType === "date") return a.answerDate ? String(a.answerDate).slice(0, 10) : "";
  if (NUMBER_VTS.has(q.valueType ?? "")) return a.answerNumber == null ? "" : String(a.answerNumber);
  return a.answerText ?? "";
};
